// In-world objects for the Belmont's Curse advanced map: what each category is, how it's drawn,
// and whether the save shows it as done. Object data comes from gamedata.json (rooms[].objects).

/**
 * Categories in legend order.
 *   tracked:  counts toward progress (collectibles and discoveries)
 *   on:       shown by default when the advanced map is first turned on
 *   completion: counts toward the game's completion % (chests and unique items through the item
 *             they hold; most doors). Exploration zones, virtues, area exits, endings and fast
 *             travel also count but have no map objects.
 *   group:    column in the advanced map's category list (see OBJECT_GROUPS)
 *   one:      singular name, for hover labels
 *   icon:     key into gameData.ui (the game's own art); otherwise a lettered badge
 *   doneIcon: art to use once done, where the game has a distinct one
 *   states:   hover wording for [done, not done], where "done" / "not yet" reads oddly
 */
export const OBJECT_TYPES = [
  { key: "healthUp", group: "collect", one: "Health upgrade", label: "Health upgrades", icon: "hpFull", tracked: true, completion: true, on: true },
  { key: "manaUp", group: "collect", one: "Mana upgrade", label: "Mana upgrades", icon: "mpFull", tracked: true, completion: true, on: true },
  { key: "atkBook", group: "collect", one: "Attack book", label: "Attack books", icon: "atkBook", glyph: "A", color: "#ff9466", tracked: true, completion: true, on: true },
  { key: "defBook", group: "collect", one: "Defence book", label: "Defence books", icon: "defBook", glyph: "D", color: "#7fb7ff", tracked: true, completion: true, on: true },
  { key: "xpAltar", group: "collect", one: "Experience altar", label: "Experience altars", icon: "xp", tracked: true, completion: true, on: true },
  { key: "chest", group: "collect", one: "Chest", label: "Chests", icon: "chest", tracked: true, completion: true, on: true },
  { key: "uniqueItem", group: "collect", one: "Unique item", label: "Unique items", glyph: "★", color: "#ffd25e", tracked: true, completion: true, on: true },
  { key: "zdoor", group: "explore", one: "Door", label: "Doors", icon: "zdoor", doneIcon: "zdoorVisited", states: ["entered", "not entered"], tracked: true, completion: true, on: true },
  { key: "secret", group: "explore", one: "Secret area", label: "Secret areas", glyph: "?", color: "#c58cff", tracked: true, on: true },
  { key: "landmark", group: "explore", one: "Landmark", label: "Landmarks", glyph: "▲", color: "#e3c46f", tracked: true, on: true },
  { key: "savePoint", group: "explore", one: "Mirror of Life", label: "Mirrors of Life", icon: "saveRoom", tracked: true, on: true },
  { key: "boss", group: "fight", one: "Boss", label: "Bosses", glyph: "B", color: "#ff5c5c", tracked: true, on: true },
  { key: "arena", group: "fight", one: "Arena fight", label: "Arena fights", glyph: "!", color: "#ff7a45", tracked: true, on: false },
  { key: "breakable", group: "explore", one: "Breakable floor", label: "Breakable floors", icon: "breakable", tracked: false, on: false },
  { key: "barricade", group: "explore", one: "Barricade", label: "Barricades", icon: "barricade", tracked: false, on: false },
  { key: "lever", group: "fight", one: "Lever", label: "Levers & switches", glyph: "L", color: "#9fd4a3", tracked: false, on: false },
  { key: "gate", group: "fight", one: "Locked gate", label: "Locked gates", glyph: "G", color: "#b9a37a", tracked: false, on: false },
  { key: "elevator", group: "explore", one: "Elevator", label: "Elevators", icon: "elevator", tracked: false, on: false },
  { key: "painting", group: "world", one: "Magical painting", label: "Magical paintings", glyph: "P", color: "#e58fd0", tracked: false, on: false },
  { key: "note", group: "world", one: "Note", label: "Notes & signs", glyph: "N", color: "#e8dcc0", tracked: false, on: false },
  { key: "clueSpirit", group: "world", one: "Clue spirit", label: "Clue spirits", glyph: "C", color: "#8fe3ff", tracked: false, on: false },
  { key: "npc", group: "world", one: "Character", label: "Characters", glyph: "☺", color: "#f2c46d", tracked: false, on: false },
  { key: "food", group: "world", one: "Food", label: "Food", glyph: "F", color: "#d98b5f", tracked: false, on: false },
]

export const OBJECT_TYPES_BY_KEY = Object.fromEntries(OBJECT_TYPES.map((t) => [t.key, t]))

/** Column headings for the advanced map's category list, in display order. */
export const OBJECT_GROUPS = [
  { key: "collect", label: "Collectibles" },
  { key: "explore", label: "Exploration" },
  { key: "fight", label: "Encounters & obstacles" },
  { key: "world", label: "World & story" },
]

/** Everything the status checks need from one save, computed once per save. */
export function objectStatusContext(save, gameData) {
  const p = save.player
  const flags = (p.progressionPersistentSave || []).map((g) => gameData.progressionKeys[g] || "")
  const norm = (s) => s.toLowerCase().replace(/[^a-z]/g, "")
  return {
    states: save.entityStates || {},
    rooms: save.rooms,
    owned: new Set(p.itemsInInventory || []),
    landmarks: new Set(p.discoveredLandmarks || []),
    defeated: flags.filter((f) => / Defeated$/.test(f)).map((f) => norm(f.replace(/ Defeated$/, ""))),
    absorbed: Object.entries(save.arcanaSteps || {})
      .filter(([, step]) => step === 4)
      .map(([guid]) => norm((gameData.characters[guid] || {}).name || ""))
      .filter(Boolean),
    norm,
  }
}

/**
 * true = done (collected, opened, found...), false = not yet, null = nothing to track.
 * Objects in rooms the save hasn't entered are "not yet".
 */
export function objectStatus(obj, roomId, ctx) {
  const state = obj.id ? ctx.states[obj.id] : null
  const room = ctx.rooms[roomId] || {}
  const activated = state && typeof state === "object" ? state.activated === true : false
  switch (obj.c) {
    case "healthUp": case "manaUp": case "atkBook": case "defBook": case "xpAltar":
    case "breakable": case "barricade": case "food":
      return activated
    case "chest": case "uniqueItem":
      return activated || (obj.item ? ctx.owned.has(obj.item) : false)
    case "gate":
    case "lever":
      return state ? activated : null
    case "arena":
      return !!(state && state.isComplete)
    case "savePoint":
      return room.usedRoomSavePoint === true
    case "secret":
      return (room.discoveredSecretZones || []).includes(obj.zone)
    case "landmark":
      return ctx.landmarks.has(obj.landmark)
    case "zdoor":
      return (room.enteredZDoorIDs || []).includes(obj.zdoor)
    case "boss": {
      // Exact "<Boss> Defeated" flag first (DeathCatacombs and Death are different fights);
      // some bosses (the Doppelganger) only show as their arcana being absorbed.
      const key = ctx.norm(obj.boss || "")
      return !!key && (ctx.defeated.includes(key) || ctx.absorbed.some((name) => key.startsWith(name)))
    }
    default:
      return null
  }
}

/**
 * A short hover label for an object. With `reveal` false, details the player hasn't seen yet
 * (a chest's contents, a landmark's name, a note's text) are left out.
 */
export function objectLabel(obj, gameData, reveal) {
  const type = OBJECT_TYPES_BY_KEY[obj.c]
  if (!reveal && ["chest", "uniqueItem", "landmark", "note"].includes(obj.c)) return type.one
  if (obj.item && gameData.items[obj.item]) return `${type.one}: ${gameData.items[obj.item].name}`
  if (obj.c === "landmark" && gameData.landmarks[obj.landmark]) return `Landmark: ${gameData.landmarks[obj.landmark]}`
  if (obj.text) {
    const text = obj.text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
    return `${type.one}: “${text.length > 200 ? `${text.slice(0, 200)}…` : text}”`
  }
  if (obj.c === "boss" && obj.boss) return `Boss: ${obj.boss.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ")}`
  if (obj.name) return `${type.one}: ${obj.name.replace(/^(GPE_|NPC|PickableItem_)/, "").replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2")}`
  return type.one
}

/** Advanced-map settings used the first time (before anything is stored). */
export function defaultAdvancedMap() {
  return { on: false, off: new Set(OBJECT_TYPES.filter((t) => !t.on).map((t) => t.key)), hideDone: false }
}
